import { useEffect, useState } from 'react'
import { PaymentOrderType, PaymentOrderTypeTitles } from 'services/apis/stripe'
import { Section, Select, Toggle } from 'stories/components'

type TAreaFlags = Partial<Record<PaymentOrderType, boolean>>
type TAreaProviders = Partial<Record<PaymentOrderType, string>>

/** The tenant's own fee on top of USIO's convenience fee: a flat amount or a % of the payment. */
export interface IAdditionalFee {
  type: 'flat' | 'percent'
  value: number
}
/** Area => method (card / us_bank_account / pinless_debit) => fee. */
type TAreaAdditionalFees = Partial<Record<PaymentOrderType, Partial<Record<string, IAdditionalFee>>>>

export interface IPaymentMethodsByArea {
  creditCardPaymentMethods?: TAreaFlags
  achBankTransferPaymentMethods?: TAreaFlags
  pinlessDebitPaymentMethods?: TAreaFlags
  creditCardPaymentProviders?: TAreaProviders
  achBankTransferPaymentProviders?: TAreaProviders
  additionalConvenienceFees?: TAreaAdditionalFees
}

export const paymentMethodsByAreaKeys: (keyof IPaymentMethodsByArea)[] = [
  'creditCardPaymentMethods',
  'achBankTransferPaymentMethods',
  'pinlessDebitPaymentMethods',
  'creditCardPaymentProviders',
  'achBankTransferPaymentProviders',
  'additionalConvenienceFees',
]

const providerTitles: Record<string, string> = { stripe: 'Stripe', usio: 'USIO' }

// Method keys match the payment method each fee applies to (StripeMethod values), written out
// rather than read off the enum at module load.
const feeColumns = [
  { title: 'Credit Card', method: 'card' },
  { title: 'ACH', method: 'us_bank_account' },
  { title: 'Pinless Debit', method: 'pinless_debit' },
] as const

const feeTypeOptions = { flat: '$ flat', percent: '% of payment' }

/**
 * One area + method's additional fee. The amount is kept locally while typing and saved on blur,
 * because every change to this grid saves the whole rules configuration.
 */
const AdditionalFeeCell = ({
  id,
  fee,
  onSave,
}: {
  id: string
  fee?: IAdditionalFee
  onSave: (fee: IAdditionalFee) => void
}) => {
  const type: IAdditionalFee['type'] = fee?.type === 'percent' ? 'percent' : 'flat'
  const [draft, setDraft] = useState(fee?.value ? String(fee.value) : '')
  useEffect(() => setDraft(fee?.value ? String(fee.value) : ''), [fee?.value])

  const commit = (nextType: IAdditionalFee['type'] = type) => {
    const value = Math.max(0, Math.round((Number(draft) || 0) * 100) / 100)
    if (value === (Number(fee?.value) || 0) && nextType === type) return
    onSave({ type: nextType, value })
  }

  return (
    <div className="flex items-center gap-2">
      <input
        id={`${id}-value`}
        type="number"
        min={0}
        step="0.01"
        placeholder="0"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit()}
        className="w-24 rounded border border-gray-300 px-2 py-1.5 text-right text-sm focus:outline-none focus:ring-2 focus:ring-shade-blue/40"
      />
      <Select
        id={`${id}-type`}
        value={type}
        options={feeTypeOptions}
        hasDefaultOption={false}
        onChange={(next: string) => commit(next === 'percent' ? 'percent' : 'flat')}
      />
    </div>
  )
}

const methodColumns = [
  { title: 'Credit Card', enabledKey: 'creditCardPaymentMethods', providerKey: 'creditCardPaymentProviders' },
  { title: 'ACH', enabledKey: 'achBankTransferPaymentMethods', providerKey: 'achBankTransferPaymentProviders' },
] as const

interface IProps {
  value: IPaymentMethodsByArea
  /** Enabled providers, key => title (Admin > Vendors) */
  providerOptions: Record<string, string>
  defaultProvider?: string
  onChange: (key: keyof IPaymentMethodsByArea, value: TAreaFlags | TAreaProviders | TAreaAdditionalFees) => void
}

/**
 * Which payment methods each area takes, and which provider runs each one. An area left on
 * "Default" follows the Default Payment Provider above it, so a single-provider tenant never has
 * to touch the provider column.
 */
export const PaymentMethodsByArea = ({ value, providerOptions, defaultProvider, onChange }: IProps) => {
  const titleOf = (provider = '') => providerOptions[provider] || providerTitles[provider] || provider
  const defaultTitle = titleOf(defaultProvider) || 'none'

  const setArea = (key: keyof IPaymentMethodsByArea, area: PaymentOrderType, next: boolean | string) => {
    onChange(key, { ...(value[key] || {}), [area]: next })
  }

  const setAdditionalFee = (area: PaymentOrderType, method: string, fee: IAdditionalFee) => {
    const fees = value.additionalConvenienceFees || {}
    onChange('additionalConvenienceFees', { ...fees, [area]: { ...(fees[area] || {}), [method]: fee } })
  }

  // A provider saved against an area stays listed after its vendor is switched off, so the grid
  // shows what the area will actually try to use instead of silently reading as "Default".
  const optionsFor = (current?: string) => ({
    '': `Default (${defaultTitle})`,
    ...providerOptions,
    ...(current && !providerOptions[current] ? { [current]: `${titleOf(current)} (vendor off)` } : {}),
  })

  return (
    <div className="col-span-full mt-4">
      <Section title="Payment Methods by Area" />
      <p className="text-sm text-gray-600 my-3">
        Turn credit card and ACH on or off for each area, and choose which provider processes each one.
      </p>

      <div className="border rounded-md p-4 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2 pr-3 font-medium">Area</th>
              {methodColumns.map((column) => (
                <th key={column.title} className="py-2 pr-3 font-medium" colSpan={2}>
                  {column.title}
                </th>
              ))}
              <th className="py-2 font-medium whitespace-nowrap">Pinless Debit (USIO only)</th>
            </tr>
          </thead>
          <tbody>
            {(Object.keys(PaymentOrderTypeTitles) as PaymentOrderType[]).map((area) => (
              <tr key={area} className="border-b last:border-b-0 align-middle">
                <td className="py-3 pr-3 whitespace-nowrap">{PaymentOrderTypeTitles[area]}</td>
                {methodColumns.map((column) => {
                  // Card and ACH default ON: only an explicit false turns an area off.
                  const enabled = value[column.enabledKey]?.[area] !== false
                  const provider = value[column.providerKey]?.[area] || ''
                  return [
                    <td key={`${column.enabledKey}-toggle`} className="py-3 pr-2">
                      <Toggle
                        id={`${column.enabledKey}-${area}`}
                        value={enabled}
                        onChange={(next: boolean) => setArea(column.enabledKey, area, next)}
                      />
                    </td>,
                    <td key={`${column.providerKey}-select`} className="py-3 pr-4 min-w-[180px]">
                      <Select
                        id={`${column.providerKey}-${area}`}
                        value={provider}
                        options={optionsFor(provider)}
                        disabled={!enabled}
                        onChange={(next: string) => setArea(column.providerKey, area, next)}
                      />
                    </td>,
                  ]
                })}
                <td className="py-3">
                  <Toggle
                    id={`pinlessDebitPaymentMethods-${area}`}
                    value={!!value.pinlessDebitPaymentMethods?.[area]}
                    onChange={(next: boolean) => setArea('pinlessDebitPaymentMethods', area, next)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Section title="Additional Convenience Fee (USIO)" className="mt-6" />
      <p className="text-sm text-gray-600 my-3">
        Your own fee, charged on top of USIO&apos;s standard convenience fee for each area and payment type: a flat
        amount or a percentage of the payment. The payer sees it as part of the convenience fee; it is not declared to
        USIO and is never applied to the loan. Leave it at 0 for none.
      </p>

      <div className="border rounded-md p-4 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2 pr-3 font-medium">Area</th>
              {feeColumns.map((column) => (
                <th key={column.method} className="py-2 pr-4 font-medium">
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(Object.keys(PaymentOrderTypeTitles) as PaymentOrderType[]).map((area) => (
              <tr key={area} className="border-b last:border-b-0 align-middle">
                <td className="py-3 pr-3 whitespace-nowrap">{PaymentOrderTypeTitles[area]}</td>
                {feeColumns.map((column) => (
                  <td key={column.method} className="py-3 pr-4">
                    <AdditionalFeeCell
                      id={`additionalConvenienceFees-${area}-${column.method}`}
                      fee={value.additionalConvenienceFees?.[area]?.[column.method]}
                      onSave={(fee) => setAdditionalFee(area, column.method, fee)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
